import { useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BackButton,
  Box,
  ButtonStyled,
  SpanStyled,
  SubHeadingStyled,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import MultiFileUploader from "../../../components/common/inputFields/fileUploader/MultifileUploader";
import {
  LeftArrowBlack,
  RightArrowWhite,
  RightArrowWithoutBar,
} from "../../../components/styled/AllImages";
import CustomRadioButton from "../../../components/common/radioButtons/CustomRadio";
import { emailRegex } from "../../../utils";
import PhoneInputField from "../../../components/common/inputFields/PhoneInput";
import ClientsApi from "../../../services/api/clientsApi";
import { toast } from "react-toastify";

const AddClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    control,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const client_type = watch("client_type");

  //   States

  const [selectedOption, setSelectedOption] = useState(client_type);
  const [loading, setLoading] = useState(false);

  // Methods and Functions

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSelectedOption(parseInt(value));
    setValue(name, parseInt(value));
  };

  const onSubmit = async (data) => {
    const formData = new FormData();

    formData.append("action", "create");

    Object.entries(data).forEach(([key, value]) => {
      if (
        !["cr", "profile", "contact_person_business_card", "others"].includes(
          key
        )
      ) {
        formData.append(key, value ?? "");
      }
    });

    if (data.cr?.length) formData.append("cr", data.cr[0]);
    if (data.profile?.length) formData.append("profile", data.profile[0]);
    if (data.contact_person_business_card?.length)
      formData.append(
        "contact_person_business_card",
        data.contact_person_business_card[0]
      );
    if (data.others?.length) formData.append("other_document", data.others[0]);

    try {
      setLoading(true);

      const response = await ClientsApi.createClient(formData);

      if (response.code === 200) {
        toast.success(t("client_created_successfully"));

        reset();
        navigate("/clients");
      } else if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("create_client")}
            onClick={() => navigate("/clients")}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <Box className="content-container">
        <FormWrapper>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="d-flex flex-column gap-2 mb-3">
              <Col md={6}>
                <SpanStyled weight="600" color="var(--text)">
                  {t("type")} *
                </SpanStyled>
              </Col>
              <Col md={6} className="type-wrapper">
                <CustomRadioButton
                  name="client_type"
                  label={t("individual")}
                  value={1}
                  checked={selectedOption === 1}
                  register={register}
                  validationRules={{
                    required: true,
                    onChange: handleChange,
                  }}
                />
                <CustomRadioButton
                  name="client_type"
                  label={t("organization")}
                  value={2}
                  checked={selectedOption === 2}
                  register={register}
                  validationRules={{
                    required: true,
                    onChange: handleChange,
                  }}
                />
              </Col>
              {errors.client_type && (
                <div style={{ color: "#dc3545", marginTop: "4px" }}>
                  {t("type_required")}
                </div>
              )}
            </Row>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="name_en"
                  type="input"
                  fieldType="text"
                  placeholder="e.g. Smart Home Hub"
                  label="name_en"
                  className="combined px-0"
                  staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="name_ar"
                  type="input"
                  fieldType="text"
                  placeholder="مثال: مركز المنزل الذكي"
                  label="name_ar"
                  className="combined px-0"
                  staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Box>
                  <ValidatedInput
                    weight="600"
                    name="email"
                    label={"email"}
                    type="input"
                    fieldType="email"
                    placeholder={"e.g. john.smith@example.com"}
                    className={"mb-3 combined flex-wrap p-0"}
                    bg="var(--white)"
                    register={register}
                    errors={errors}
                    validationRules={{
                      required: false,
                      inline: true,
                      pattern: {
                        value: emailRegex,
                        message: "invalidEmail",
                      },
                    }}
                  />
                </Box>
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="tax_registration_number"
                  type="input"
                  fieldType="number"
                  placeholder="e.g. 0000000000000000"
                  label="tax_registration_number"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
            </Row>
            <SubHeadingStyled
              size="20px"
              weight="700"
              color="var(--text)"
              className="mt-3"
            >
              {t("Address")}
            </SubHeadingStyled>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="address"
                  type="input"
                  fieldType="text"
                  placeholder="e.g. 7642 Prince Sultan Street, Al Rawdah District, Jeddah 23434, KSA"
                  label="Address"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="city"
                  type="input"
                  fieldType="text"
                  placeholder="e.g. Riyadh"
                  label="city"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="street_name"
                  type="input"
                  fieldType="text"
                  placeholder="e.g. Sultan Street"
                  label="street_name"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="sub_number"
                  type="input"
                  fieldType="number"
                  placeholder="Sub Number (additional Number)"
                  label="sub_number"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="postal_code"
                  type="input"
                  fieldType="number"
                  placeholder="e.g. 12211"
                  label="postal_code"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="commercial_registration_number"
                  type="input"
                  fieldType="number"
                  placeholder="e.g. 1012345678"
                  label="commercial_registration_number"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="building_number"
                  type="input"
                  fieldType="number"
                  placeholder="e.g. 658"
                  label="building_number"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="region"
                  type="input"
                  fieldType="text"
                  placeholder="e.g. Riyadh Region"
                  label="region"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
            </Row>
            <SubHeadingStyled
              size="20px"
              weight="700"
              color="var(--text)"
              className="mt-3"
            >
              {t("contact_information")}
            </SubHeadingStyled>
            <Row>
              <Col md={6}>
                <PhoneInputField
                  weight="600"
                  className={"flex-wrap p-0"}
                  bg="var(--white)"
                  control={control}
                  name="fax_number"
                  placeholder="+966 000 000 000"
                  label="fax_number"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                />
              </Col>
              <Col md={6}>
                <PhoneInputField
                  weight="600"
                  name="phone"
                  label={"phone"}
                  placeholder={"+966 000 000 000"}
                  register={register}
                  control={control}
                  errors={errors}
                  validationRules={{
                    required: false,
                  }}
                  className={"flex-wrap p-0"}
                  bg="var(--white)"
                />
              </Col>
            </Row>
            <PhoneInputField
              weight="600"
              column={true}
              start={true}
              name="mobile"
              placeholder="+966 000 000 000"
              label="mobile"
              className="combined px-0"
              control={control}
              // staric="staric"
              register={register}
              errors={errors}
              bg="var(--white)"
              validationRules={{
                required: false,
              }}
            />

            <SubHeadingStyled
              size="20px"
              weight="700"
              color="var(--text)"
              className="mt-3"
            >
              {t("Attachments")}
            </SubHeadingStyled>
            <Row className="mb-4">
              <Col md={6} sm={12}>
                <MultiFileUploader
                  weight="600"
                  width="100% !important"
                  name="cr"
                  control={control}
                  rules={{ required: false }}
                  label={"cr"}
                  acceptType="application/pdf"
                  maxFiles={1}
                />
              </Col>
              <Col md={6} sm={12}>
                <MultiFileUploader
                  weight="600"
                  width="100% !important"
                  name="profile"
                  control={control}
                  rules={{ required: false }}
                  label={"profile"}
                  acceptType="image"
                  maxFiles={1}
                />
              </Col>
            </Row>
            <Row className="mb-4">
              <Col md={6} sm={12}>
                <MultiFileUploader
                  weight="600"
                  width="100% !important"
                  name="contact_person_business_card"
                  control={control}
                  rules={{ required: false }}
                  label={"contact_person_business_card"}
                  acceptType="image"
                  maxFiles={1}
                />
              </Col>
              <Col md={6} sm={12}>
                <MultiFileUploader
                  weight="600"
                  width="100% !important"
                  name="other_document"
                  control={control}
                  rules={{ required: false }}
                  label={"others"}
                  acceptType="all"
                  maxFiles={1}
                />
              </Col>
            </Row>

            <FooterButtons>
              <ButtonStyled
                border="var(--borderExtraLight)"
                iconBefore={LeftArrowBlack}
                bg="transparent"
                label={t("Cancel")}
                color="var(--text)"
                type="button"
                onClick={() => navigate(-1)}
              />
              <ButtonStyled
                icon={RightArrowWhite}
                type="submit"
                label={t("create")}
                isLoading={loading}
                bg="var(--themeColor)"
              />
            </FooterButtons>
          </Form>
        </FormWrapper>
      </Box>
    </>
  );
};

export default AddClient;

const FormWrapper = styled.div`
  padding: 30px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  .type-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;

    @media (max-width: 460px) {
      flex-direction: column;
    }
  }
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;
